//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React from 'react';
import { useTranslation } from 'react-i18next';
import MemberAccessLink from './MemberAccessLink';
import { IntegrationAccessIssue } from '../../../types/integrationPreferences';

/**
 * Shown in Loop Genie to a user with no usable AI integration: in no group, or in groups
 * whose integrations are all disabled. The remedy differs (join a group, or have an
 * administrator enable one), so the text does too. Without it the chat just fails with no
 * hint as to why.
 */
const NoIntegrationAccessNotice: React.FC<{
  reason: IntegrationAccessIssue;
}> = ({ reason }) => {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      data-testid="no-integration-access-notice"
      className="mx-4 mb-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      {reason === 'all_disabled'
        ? t(
            'userGroups.allDisabledNotice',
            'All AI integrations are disabled for your groups. Ask an administrator to enable one.',
          )
        : t(
            'userGroups.noGroupNotice',
            "You're not in any group, so you have no active AI integrations. Ask an administrator to add you to a group.",
          )}{' '}
      <MemberAccessLink>
        {t('userGroups.openMemberAccess', 'Open Member Access')}
      </MemberAccessLink>
    </div>
  );
};

export default NoIntegrationAccessNotice;
